import { Stack } from '@mantine/core';
import cn from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';

import Container from 'components/ui/Container';
import { useLkLayoutStore } from 'stores/local/layout/LkLayoutStore';
import { useDevices } from 'utils/devices';

import Header from './Header';
import LogoutButton from './LogoutButton';
import Menu from './Menu';
import { MenuBottomLinks } from './MenuBottomLinks';
import MobileDrawer from './MobileDrawer';
import SocialLinks from './SocialLinks';
import TeamResponsible from './TeamResponsible';
import { useScrollShadows } from './hooks/useScrollShadows';

import s from './SideMenu.module.scss';

export const SideMenu: React.FC = () => {
  const { menu } = useLkLayoutStore();
  const { isPhone, isTablet } = useDevices();
  const { scrollRef, showTopShadow, showBottomShadow, onScroll } = useScrollShadows();

  React.useEffect(() => {
    if (isPhone || isTablet) {
      menu.close();
    }
  }, [isPhone, isTablet, menu]);

  const handleTabletInteraction = React.useCallback(
    (e: React.MouseEvent) => {
      if (!isTablet || !menu.isOpen) {
        return;
      }

      const target = e.target as HTMLElement;
      const interactive = target.closest('a, button');

      if (!interactive) {
        return;
      }

      // Кнопка открытия/закрытия меню
      if (interactive.closest('[data-sidebar-toggle]')) {
        return;
      }

      menu.close();
    },
    [isTablet, menu],
  );

  if (isPhone) {
    return <MobileDrawer />;
  }

  const menuContent = (
    <Container
      className={cn(s.root, !menu.isOpen && s.root_collapsed, isTablet && s.root_tablet)}
      onClick={isTablet ? handleTabletInteraction : undefined}
    >
      <div className={cn(s.root__shadow, showTopShadow && s.root__shadow_visible)} aria-hidden />
      <div className={s.root__scrollArea}>
        <div ref={scrollRef} className={s.root__scroll} onScroll={onScroll}>
          <Stack align={menu.isOpen ? 'flex-start' : 'center'}>
            <Header />
            <TeamResponsible />
            <Menu />
            <MenuBottomLinks />
            <SocialLinks />
          </Stack>
        </div>
        <div className={cn(s.root__shadowBottom, showBottomShadow && s.root__shadowBottom_visible)} aria-hidden />
      </div>
      <div className={cn(s.root__footer, !menu.isOpen && s.root__footer_collapsed)}>
        <LogoutButton className={s.root__logout} />
      </div>
    </Container>
  );

  if (isTablet) {
    return <div className={s.tabletSlot}>{menuContent}</div>;
  }

  return menuContent;
};

export default observer(SideMenu);
